import { Component, computed, EventEmitter, input, Output, signal } from '@angular/core';
import { UploadedFile } from '../../interfaces/new-vehicle/new-vehicle.interface';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  standalone: true,
})
export class FileUploadComponent {

  // Input para número máximo de archivos
  maxFiles = input<number>(10); // Valor por defecto: 10 archivos

  // Archivos subidos
  uploadedFiles = signal<UploadedFile[]>([]);

  // Estado de arrastre
  isDragging = signal<boolean>(false);

  // Mensaje de error
  errorMessage = signal<string>('');

  @Output() filesChanged = new EventEmitter<UploadedFile[]>();
  @Output() disableButton = new EventEmitter<boolean>(false);

  // Configuración
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  readonly ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
  readonly ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg'];

  // Computed: Verificar si hay archivos completados
  hasCompletedFiles = computed(() => {
    this.filesChanged.emit(this.uploadedFiles());
    return this.uploadedFiles().some(file => file.status === 'completed');
  });

  // Computed: Verificar si puedo continuar
  canContinue = computed(() => {
    const files = this.uploadedFiles();
    return files.some(f => f.status === 'completed') &&
      !files.some(f => f.status === 'uploading');
  });

  // Computed: Verificar si se alcanzó el límite de archivos
  hasReachedMaxFiles = computed(() => {
    return this.uploadedFiles().length >= this.maxFiles();
  });

  // Manejar selección de archivos desde input
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
      input.value = '';
    }
  }

  // Manejar drag over
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  // Manejar drag leave
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  // Manejar drop
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  // Procesar archivos
  handleFiles(files: File[]): void {
    this.errorMessage.set('');

    // Verificar límite de archivos
    const currentFilesCount = this.uploadedFiles().length;
    const availableSlots = this.maxFiles() - currentFilesCount;

    if (availableSlots <= 0) {
      this.errorMessage.set(`Has alcanzado el límite máximo de ${this.maxFiles()} archivos.`);
      return;
    }

    if (files.length > availableSlots) {
      this.errorMessage.set(`Solo puedes agregar ${availableSlots} archivo(s) más. Límite máximo: ${this.maxFiles()}`);
      files = files.slice(0, availableSlots);
    }

    for (const file of files) {
      // Validar tipo de archivo
      if (!this.ALLOWED_TYPES.includes(file.type)) {
        this.errorMessage.set(`El archivo "${file.name}" no es un formato válido. Solo se permiten PDF, PNG y JPEG.`);
        continue;
      }

      // Validar tamaño
      if (file.size > this.MAX_FILE_SIZE) {
        this.errorMessage.set(`El archivo "${file.name}" excede el tamaño máximo de 5MB.`);
        continue;
      }

      // Agregar archivo a la lista
      const uploadedFile: UploadedFile = {
        id: crypto.randomUUID(),
        file: file,
        name: file.name,
        size: file.size,
        progress: 0,
        status: 'uploading'
      };

      this.uploadedFiles.update(files => [...files, uploadedFile]);

      // Iniciar simulación de carga
      this.simulateUpload(uploadedFile.id);
    }
  }

  // Convertir archivo a Base64
  convertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Content = base64.split(',')[1];
        resolve(base64Content);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  // Convertir archivo a Base64 completo
  convertToBase64Full(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  // Simular progreso de carga y convertir a base64
  async simulateUpload(fileId: string): Promise<void> {
    const fileData = this.uploadedFiles().find(f => f.id === fileId);
    if (!fileData) return;

    try {
      this.disableButton.emit(true);
      const base64Content = await this.convertToBase64(fileData.file);

      let progress = 0;

      const interval = setInterval(() => {
        progress += Math.random() * 15;

        if (progress >= 100) {
          progress = 100;

          this.uploadedFiles.update(files =>
            files.map(f =>
              f.id === fileId
                ? {
                  ...f,
                  progress: 100,
                  status: 'completed' as const,
                  base64: base64Content,
                  mimeType: f.file.type
                }
                : f
            )
          );

          clearInterval(interval);
          if (this.canContinue()) {
            this.disableButton.emit(false);
          }
        } else {
          this.uploadedFiles.update(files =>
            files.map(f =>
              f.id === fileId
                ? { ...f, progress: Math.floor(progress) }
                : f
            )
          );
        }
      }, 200);
    } catch (error) {
      console.error('Error al convertir a base64:', error);
      this.uploadedFiles.update(files =>
        files.map(f =>
          f.id === fileId
            ? {
              ...f,
              status: 'error' as const,
              errorMessage: 'Error al procesar el archivo'
            }
            : f
        )
      );
    }
  }

  // Cancelar carga
  cancelUpload(fileId: string): void {
    this.uploadedFiles.update(files =>
      files.filter(f => f.id !== fileId)
    );
  }

  // Eliminar archivo
  removeFile(fileId: string): void {
    this.uploadedFiles.update(files =>
      files.filter(f => f.id !== fileId)
    );
  }
}
